import { useState } from "react";
import { useDispatch } from "react-redux";
import { Button, InputNumber, Select, Typography } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import type { NoteAttribute } from "@store/reducers/notes.reducer";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { createNoteAttribute, getNoteAttributes } from "@store/actions/notes.actions";
import { readNoteApiError } from "@utils/noteApiError.mjs";
import { LIMITS, formatAttributeRange, missingSuggestedAttributes } from "@utils/notesForm.mjs";
import type { NoteFormState } from "@utils/notesForm.mjs";
import { NoteAttributeCreateForm } from "./NoteAttributeCreateForm";

type AttributeRow = NoteFormState["attributeRows"][number];

const CREATE_OPTION = "create-attribute";
const SUGGESTION_PREFIX = "suggested:";

interface NoteAttributesFieldProps {
    rows: AttributeRow[];
    attributes: NoteAttribute[];
    /** One message for each row, or null. */
    errors: (string | null | undefined)[];
    disabled?: boolean;
    onChange: (rows: AttributeRow[]) => void;
}

export const NoteAttributesField = ({ rows, attributes, errors, disabled, onChange }: NoteAttributesFieldProps) => {
    const dispatch = useDispatch<any>();
    const [creatingRow, setCreatingRow] = useState<number | null>(null);
    const [addingSuggestionRow, setAddingSuggestionRow] = useState<number | null>(null);
    const suggestions = missingSuggestedAttributes(attributes);

    const updateRow = (index: number, patch: Partial<AttributeRow>) =>
        onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

    const removeRow = (index: number) => {
        setCreatingRow(null);
        onChange(rows.filter((_, i) => i !== index));
    };

    // A suggested attribute does not exist yet. Picking it creates it, and the row then uses the new attribute.
    // When the create fails, the row stays empty.
    const addSuggestion = async (index: number, name: string) => {
        const suggestion = suggestions.find((s) => s.name === name);
        if (!suggestion) return;
        setAddingSuggestionRow(index);
        try {
            const created = await dispatch(createNoteAttribute(suggestion));
            updateRow(index, { attributeId: created.id });
        } catch (e) {
            const { message: text, field } = readNoteApiError(e);
            // The name is taken: someone created it after this list loaded. Reload the list and use theirs.
            if (field === "name") {
                const latest: NoteAttribute[] = await dispatch(getNoteAttributes()).catch(() => []);
                const existing = latest.find((a) => a.name.toLowerCase() === name.toLowerCase());
                if (existing) {
                    updateRow(index, { attributeId: existing.id });
                    return;
                }
            }
            // The thunk shows an error that has no field. This picker has no field to show one next to, so it shows it here.
            if (field !== null) message.error(text);
        } finally {
            setAddingSuggestionRow(null);
        }
    };

    const handleSelect = (index: number, value: number | string) => {
        if (value === CREATE_OPTION) {
            setCreatingRow(index);
            return;
        }
        if (typeof value === "string" && value.startsWith(SUGGESTION_PREFIX)) {
            setCreatingRow(null);
            addSuggestion(index, value.slice(SUGGESTION_PREFIX.length));
            return;
        }
        setCreatingRow(null);
        updateRow(index, { attributeId: value as number });
    };

    const optionsFor = (index: number) => {
        const usedElsewhere = new Set(rows.filter((_, i) => i !== index).map((r) => r.attributeId));
        return [
            ...suggestions.map((s) => ({ value: `${SUGGESTION_PREFIX}${s.name}`, label: `${s.name} (${formatAttributeRange(s.min_value, s.max_value)})` })),
            ...attributes.filter((a) => !usedElsewhere.has(a.id)).map((a) => ({ value: a.id, label: a.name })),
            { value: CREATE_OPTION, label: "+ New attribute" },
        ];
    };

    const atLimit = rows.length >= LIMITS.attributes;

    return (
        <div>
            {rows.map((row, index) => {
                const attribute = attributes.find((a) => a.id === row.attributeId);
                return (
                    // Rows have no id of their own. The list only grows at the end or loses one row, so the index is stable enough.
                    <div key={index}>
                        <div className="notes-dialog-attribute-row">
                            <Select
                                className="notes-dialog-attribute-select"
                                showSearch
                                disabled={disabled || addingSuggestionRow === index}
                                loading={addingSuggestionRow === index}
                                placeholder="Choose an attribute"
                                value={attribute ? attribute.id : null}
                                options={optionsFor(index)}
                                filterOption={(input, option) =>
                                    option?.value === CREATE_OPTION || String(option?.label).toLowerCase().includes(input.toLowerCase())
                                }
                                onChange={(value) => handleSelect(index, value)}
                            />
                            <InputNumber
                                className="notes-dialog-attribute-value"
                                disabled={disabled || !attribute}
                                placeholder="Value"
                                value={row.value}
                                onChange={(value) => updateRow(index, { value })}
                            />
                            {attribute && (
                                <Typography.Text type="secondary">{formatAttributeRange(attribute.min_value, attribute.max_value)}</Typography.Text>
                            )}
                            <Button
                                type="text"
                                size="small"
                                disabled={disabled}
                                icon={<CloseOutlined />}
                                aria-label="Remove attribute"
                                title="Remove attribute"
                                data-attr="notes.note-dialog.remove-attribute"
                                onClick={() => removeRow(index)}
                            />
                        </div>
                        {errors[index] && <Typography.Text type="danger">{errors[index]}</Typography.Text>}
                        {creatingRow === index && (
                            <NoteAttributeCreateForm
                                onCreated={(created) => {
                                    updateRow(index, { attributeId: created.id });
                                    setCreatingRow(null);
                                }}
                                onCancel={() => setCreatingRow(null)}
                            />
                        )}
                    </div>
                );
            })}
            <Button
                type="link"
                size="small"
                disabled={disabled || atLimit}
                onClick={() => onChange([...rows, { attributeId: null, value: null }])}
                data-attr="notes.note-dialog.add-attribute"
            >
                {atLimit ? `A note can have at most ${LIMITS.attributes} attributes.` : "+ Add attribute"}
            </Button>
        </div>
    );
};
